import React from 'react'

import type { PagesFilters } from '../../lib/pagesFilters'

type Props = {
  open: boolean
  onClose: () => void
  filters: PagesFilters
  authed: boolean
  categories: string[]
  onApply: (next: PagesFilters) => void
  onReset: () => void
}

export default function PagesFilterDrawer({ open, onClose, filters, authed, categories, onApply, onReset }: Props) {
  const [local, setLocal] = React.useState<PagesFilters>(filters)
  React.useEffect(() => setLocal(filters), [filters])

  const selectedCategoryCount = local.categories.length

  function toggleCategory(category: string) {
    const normalized = category.trim().toLowerCase()
    setLocal((prev) => {
      const has = prev.categories.includes(normalized)
      return {
        ...prev,
        categories: has ? prev.categories.filter((c) => c !== normalized) : [...prev.categories, normalized],
      }
    })
  }

  function clear() {
    onReset()
    onClose()
  }

  return (
    <div aria-hidden={!open}>
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed right-0 top-0 h-full w-[360px] md:w-[420px] bg-neutral-900 border-l border-white/10 shadow-2xl z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="text-base font-extrabold">Filters</div>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white" aria-label="Close filters">x</button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          <div>
            <div className="text-sm text-white/70 mb-2">Visibility</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setLocal((p) => ({ ...p, boostedOnly: !p.boostedOnly, mineOnly: p.boostedOnly ? p.mineOnly : false }))}
                className={`px-3 py-1.5 rounded-full border text-xs ${
                  local.boostedOnly
                    ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                    : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                }`}
              >
                Show boosted only
              </button>
              {authed ? (
                <button
                  type="button"
                  onClick={() => setLocal((p) => ({ ...p, mineOnly: !p.mineOnly, boostedOnly: p.mineOnly ? p.boostedOnly : false }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.mineOnly
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  Show my businesses only
                </button>
              ) : null}
              {authed ? (
                <button
                  type="button"
                  onClick={() => setLocal((p) => ({ ...p, includePending: !p.includePending }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.includePending
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  Include pending
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setLocal((p) => ({ ...p, openNow: !p.openNow }))}
                className={`px-3 py-1.5 rounded-full border text-xs ${
                  local.openNow
                    ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                    : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                }`}
              >
                Open now
              </button>
              <button
                type="button"
                onClick={() => setLocal((p) => ({ ...p, bookableToday: !p.bookableToday }))}
                className={`px-3 py-1.5 rounded-full border text-xs ${
                  local.bookableToday
                    ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                    : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                }`}
              >
                Bookable today
              </button>
              <button
                type="button"
                onClick={() => setLocal((p) => ({ ...p, hasBooking: !p.hasBooking }))}
                className={`px-3 py-1.5 rounded-full border text-xs ${
                  local.hasBooking
                    ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                    : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                }`}
              >
                Has booking
              </button>
            </div>
            {!authed ? (
              <div className="mt-2 text-xs text-white/60">
                Sign in to filter by your businesses and pending status.
              </div>
            ) : null}
            <div className="mt-2 text-xs text-white/60">
              Boosted and My Businesses stay as separate sections.
            </div>
          </div>

          <div>
            <div className="text-sm text-white/70 mb-2">Categories {selectedCategoryCount > 0 ? `(${selectedCategoryCount})` : ''}</div>
            <div className="max-h-52 overflow-y-auto space-y-2 rounded-lg border border-white/10 bg-black/20 p-3">
              {categories.map((category) => {
                const normalized = category.trim().toLowerCase()
                return (
                  <label key={category} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={local.categories.includes(normalized)} onChange={() => toggleCategory(category)} />
                    <span>{category}</span>
                  </label>
                )
              })}
            </div>
          </div>

          <div>
            <div className="text-sm text-white/70 mb-2">Sort by</div>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'newest', label: 'Newest' },
                { key: 'soonest', label: 'Soonest available' },
                { key: 'reputation', label: 'Rating' },
                { key: 'distance', label: 'Distance' },
                { key: 'alphabetical', label: 'Alphabetical (A-Z)' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setLocal((p) => ({ ...p, sort: opt.key }))}
                  className={`px-3 py-1.5 rounded-full border text-xs ${
                    local.sort === opt.key
                      ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200'
                      : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between">
            <button
              type="button"
              onClick={clear}
              className="text-sm text-white/70 hover:text-white"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                onApply(local)
                onClose()
              }}
              className="px-3 py-2 rounded-lg bg-brand/80 hover:bg-brand text-sm font-semibold"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}




